import { useMemo } from 'react'
import type { Row } from '@solum/sdk'
import {
  groupConnections, isTimelineRelationship,
  type ConnectionArrangement, type ConnectionCount, type ConnectionGroup,
  type ConnectionItem, type ConnectionReading,
} from '@solum/experience'
import { useRecordIdentities, useRelated, useSchema } from '../data/queries'
import { endpointPairs, isSymmetric } from './relations'
import { useRelatedCounts } from './useRelations'
import { rungFrom, useRelatedOrder } from './useRelatedOrder'

/**
 * The console's HOST half of the shared Connections surface (#1466).
 *
 * `@solum/ui`'s `Connections` is data-free and `@solum/experience`'s
 * `groupConnections` is pure, because every question this file answers is a
 * governed one: what the caller may NAME (`core.titles_of`, caller-RLS), how
 * many there ARE (`core.related_counts`, capped and honest about it), and how
 * they should be ARRANGED (the `core.related_order` cascade). A presentational
 * component that could answer any of those would be able to get one wrong.
 *
 * What this replaces, in `ShellDrawer.tsx:389`:
 *   · groups keyed by `rel_type` with the direction thrown away, so an
 *     organisation's inbound `works_at` read as "Works at" rather than "Employs";
 *   · headings rendered as `rel_type.replace(/_/g,' ')` — the storage key;
 *   · a count that was the number LOADED, presented as the number that is;
 *   · `namedFact` hard-coded to `works_at` → `attributes.title`;
 *   · and rows that were buttons for exactly three entity types and dead for
 *     every other one.
 */

/** `related_counts.slot_id` is `<relType>:<direction>:<otherEntity>`. */
export function countsFrom(bySlot: Map<string, { n: number; capped: boolean }>): ConnectionCount[] {
  const out: ConnectionCount[] = []
  for (const [slot, c] of bySlot) {
    const [relType, direction, entity] = slot.split(':')
    // A slot the door names in a shape this does not recognise is DROPPED, not
    // guessed at: a count attributed to the wrong group is worse than no count.
    if (!relType || !entity) continue
    if (direction !== 'out' && direction !== 'in' && direction !== 'both') continue
    out.push({ relType, reading: direction, entity, n: c.n, capped: c.capped })
  }
  return out
}

/**
 * Slot-shaped arrangement → group-shaped arrangement.
 *
 * The `core.related_order` document states positions per SLOT
 * (`relType:direction:otherEntity`); a connection GROUP is one reading of one
 * relationship, which is one level up. So a group is pinned when the person
 * pinned ANY slot in it, and hidden only when they hid EVERY slot in it —
 * hiding "People at Acme" must not silently hide "Employs".
 */
export function connectionArrangement(
  groups: readonly ConnectionGroup[], rung: ReturnType<typeof rungFrom>,
): ConnectionArrangement {
  const pinned: { key: string; at: number }[] = []
  const hidden: string[] = []
  for (const g of groups) {
    const slots = g.byType.map((t) => `${g.relType}:${g.reading}:${t.entity}`)
    const stated = slots.map((s) => rung.statedOf(s)).filter((n): n is number => n !== null)
    if (stated.length) pinned.push({ key: g.key, at: Math.min(...stated) })
    if (slots.length && slots.every((s) => rung.isHidden(s))) hidden.push(g.key)
  }
  return {
    pinned: pinned.sort((a, b) => a.at - b.at).map((p) => p.key),
    hidden,
  }
}

export interface ConsoleConnections {
  groups: ConnectionGroup[]
  arrangement: ConnectionArrangement
  busy: boolean
  problem: string | null
}

/**
 * `enabled` exists for the record page's LIST lens (#1466). There, the counts
 * come from `core.related_counts` and the rows from each slot's own grid, so
 * the two edge reads below buy nothing until somebody asks for the metro
 * picture — and two reads per record page that nothing renders is a cost the
 * drawer was never paying. The groups are still correct while it is off: a
 * group's `count` is the server's, never `items.length`, whenever the counts
 * door answered.
 */
export function useConnections(entity: string, id: string, enabled = true): ConsoleConnections {
  // Both ends, because the READING is the label — one `both` traversal would
  // collapse "Works at" and "Employs" into one heading.
  const out = useRelated('core', 'relationship', { from_type: entity, from_id: id }, enabled)
  const inn = useRelated('core', 'relationship', { to_type: entity, to_id: id }, enabled)
  const schema = useSchema()
  const counts = useRelatedCounts(entity, id)
  const order = useRelatedOrder(entity)

  const symmetric = useMemo(() => new Set((schema.data?.relationships ?? [])
    .filter((rt) => endpointPairs(rt, schema.data?.relationship_pairs ?? [])
      .some((pair) => pair.from === entity && pair.to === entity
        && isSymmetric(rt, pair.from, pair.to)))
    .map((rt) => rt.key)), [schema.data, entity])

  const edges = useMemo(() => {
    const rows = [
      ...((out.data ?? []) as Row[]).map((e) => ({ e, reading: 'out' as ConnectionReading,
        entity: String(e.to_type), id: String(e.to_id) })),
      ...((inn.data ?? []) as Row[]).map((e) => ({ e, reading: 'in' as ConnectionReading,
        entity: String(e.from_type), id: String(e.from_id) })),
    ].map((row) => symmetric.has(String(row.e.rel_type))
      ? { ...row, reading: 'both' as ConnectionReading }
      : row)
    // Timeline edges are dropped by `groupConnections` — the Timeline owns them,
    // and showing them here makes one interaction look like two facts. Dropping
    // them HERE too is the difference between not showing them and paying a
    // `core.titles_of` resolution per activity to not show them.
    return rows.filter((x) => !isTimelineRelationship(String(x.e.rel_type))
      // The governed symmetric list is a list of OTHER records. A self-link is
      // storage history, not a partner, and the server count excludes it too.
      && !(x.reading === 'both' && x.entity === entity && x.id === id))
  }, [out.data, inn.data, symmetric, entity, id])

  const refs = useMemo(() => edges.map((x) => ({ type: x.entity, id: x.id })), [edges])
  const records = useRecordIdentities(refs)

  const items = useMemo<ConnectionItem[]>(() => edges.map((x) => {
    const identity = records.identity(x.entity, x.id)
    const attrs = (x.e.attributes as Row | undefined) ?? {}
    // The facts a LINK carries are declared per relationship type
    // (`relationship_type.attributes_schema`), so they are read off the edge
    // rather than named here. The old panel hard-coded `works_at → title`,
    // which is one module's vocabulary in the shell's chrome.
    const facts = Object.entries(attrs)
      .filter(([, v]) => v != null && v !== '')
      .map(([, v]) => String(v))
    return {
      key: String(x.e.id),
      relType: String(x.e.rel_type),
      reading: x.reading,
      entity: x.entity,
      id: x.id,
      title: identity.resolved ? identity.title : null,
      subtitle: identity.subtitle,
      iconKey: identity.iconKey,
      nameable: identity.resolved,
      openable: identity.resolved && records.canNavigate(x.entity),
      ended: x.e.valid_to != null,
      facts,
    }
  }), [edges, records])

  const groups = useMemo(
    () => groupConnections(items, schema.data, countsFrom(counts.bySlot)),
    [items, schema.data, counts.bySlot])

  const arrangement = useMemo(
    () => connectionArrangement(groups, rungFrom(order.document)), [groups, order.document])

  return {
    groups,
    arrangement,
    busy: out.isLoading || inn.isLoading,
    // The COUNTS door failing is not the panel failing: the rows are still
    // true, they are just no longer known to be all of them.
    problem: out.isError || inn.isError ? 'Connections could not be read.' : null,
  }
}
